import type { TProcessingAction, TPublisherRequest, TDraftReviewSong } from '@modules/types'

export type ApprovalState = {
  editingState: Record<string, boolean>
  feeQuotedPriceValues: Record<string, string>
  processingSongId: number | null
  processingAction: TProcessingAction
}

export type ApprovalAction =
  | { type: 'SYNC_FROM_REQUEST'; payload: { selectedRequest?: TPublisherRequest } }
  | { type: 'SET_EDITING'; payload: { key: string; value: boolean } }
  | { type: 'SET_VALUE'; payload: { key: string; value: string } }
  | { type: 'CLEAR_KEYS'; payload: { keys: string[] } }
  | { type: 'SET_PROCESSING'; payload: { songId: number; action: TProcessingAction } }
  | { type: 'CLEAR_PROCESSING' }

export const initialState: ApprovalState = {
  editingState: {},
  feeQuotedPriceValues: {},
  processingSongId: null,
  processingAction: null,
}

export const approvalReducer = (state: ApprovalState, action: ApprovalAction): ApprovalState => {
  switch (action.type) {
    case 'SYNC_FROM_REQUEST': {
      const { selectedRequest } = action.payload
      if (!selectedRequest?.draftReview?.songs) {
        return initialState
      }

      const feeQuotedPriceValues: Record<string, string> = {}
      const validKeys = new Set<string>()

      selectedRequest.draftReview.songs.forEach(song => {
        const draftSong = song as TDraftReviewSong

        if (draftSong.printCounterOfferFee != null) {
          const key = `${song.songId}_print`
          feeQuotedPriceValues[key] = draftSong.printCounterOfferFee.toString()
          validKeys.add(key)
        }

        if (draftSong.digitalCounterOfferFee != null) {
          const key = `${song.songId}_digital`
          feeQuotedPriceValues[key] = draftSong.digitalCounterOfferFee.toString()
          validKeys.add(key)
        }
      })

      const editingState = Object.fromEntries(
        Object.entries(state.editingState).filter(([key]) => validKeys.has(key)),
      )

      return {
        ...state,
        editingState,
        feeQuotedPriceValues,
      }
    }
    case 'SET_EDITING':
      return {
        ...state,
        editingState: {
          ...state.editingState,
          [action.payload.key]: action.payload.value,
        },
      }
    case 'SET_VALUE':
      return {
        ...state,
        feeQuotedPriceValues: {
          ...state.feeQuotedPriceValues,
          [action.payload.key]: action.payload.value,
        },
      }
    case 'CLEAR_KEYS': {
      const nextEditingState = { ...state.editingState }
      action.payload.keys.forEach(key => {
        delete nextEditingState[key]
      })
      return {
        ...state,
        editingState: nextEditingState,
      }
    }
    case 'SET_PROCESSING':
      return {
        ...state,
        processingSongId: action.payload.songId,
        processingAction: action.payload.action,
      }
    case 'CLEAR_PROCESSING':
      return {
        ...state,
        processingSongId: null,
        processingAction: null,
      }
    default:
      return state
  }
}
